import axios, { AxiosResponse } from 'axios';
import { useLoadingStore } from '@src/store/common/LoadingStore';
import { useOverlay } from '@abyss/web/hooks/useOverlay';
import { CookieService } from './CookieService';
import { ApiMessages } from '@src/constants/ApiMessage';
import { ApiStatus } from '@src/constants/ApiStatus';
import { useRouter } from '@abyss/web/hooks/useRouter';

export type ApiResponse = {
  data: any;
  message: string;
  status?: number;
};

const HttpService = () => {
  const loadingState = useLoadingStore((state) => state.loadingState);
  const setLoading = useLoadingStore((state) => state.setLoading);
  const loadingOverlay = useOverlay('crdp-loading');
  const { navigate } = useRouter();

  const GetHeaders = (): any => {
    const token = CookieService.GetCookie('authToken');
    return {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
  };


  const response: ApiResponse = { data: null, message: '', status: 0 };

  const ErrorResponse = (err: any): ApiResponse => {
    response.data = err.response?.data || null;

    if (err.response?.status === 401) {
      response.message = ApiMessages.SessionExpired;
      response.status = 401;

      // Get current location to avoid redirect loops
      const currentPath = window.location.pathname;

      // Don't redirect if we're already on authentication-related pages
      const authPages = ['/login', '/sso', '/not-authenticated', '/logout'];
      const isOnAuthPage = authPages.includes(currentPath);

      if (!isOnAuthPage) {
        // Check if user had SSO auth BEFORE clearing cookies
        const hadSSOAuth =
          CookieService.GetCookie('authUser') ||
          CookieService.GetCookie('authToken');

        // Clear authentication state on 401
        localStorage.removeItem('isLoggedIn');
        CookieService.DeleteCookie('authToken');
        CookieService.DeleteCookie('authUser');

        // Redirect based on previous authentication method
        if (hadSSOAuth) {
          navigate('/sso');
        } else {
          navigate('/login');
        }
      }
    } else if (err.response?.status === 403) {
      response.message = ApiMessages.AccessDenied;
      response.status = 403;
      navigate('/access-denied');
    } else if (err.response?.status === 404) {
      response.message = ApiMessages.NotFound;
      response.status = 404;
      navigate('/not-found');
    } else {
      response.message = err.message;
      response.status = err.response?.status;
    }

    return response;
  };

  const Response = (res: AxiosResponse<any, any>): ApiResponse => {
    if (
      (res.status === 200 || res.status === 201) &&
      res.data?.hasError === false &&
      res.data?.hasWarning === false
    ) {
      response.data = res.data;
      response.message = ApiMessages.Success;
      response.status = res.status;
    } else if (
      (res.status === 200 || res.status === 201) &&
      (res.data?.hasError === true || res.data?.hasWarning === true)
    ) {
      response.data = res.data;
      response.message = ApiMessages.Error;
      response.status = ApiStatus.Error;
    } else {
      response.data = res.data;
      response.message = ApiMessages.Error;
      response.status = res.status;
    }
    return response;
  };

  const GetLoaderState = (isLoading: boolean) => {
    if (isLoading === true) {
      loadingState.count += 1;
    } else {
      loadingState.count -= 1;
    }
    loadingState.loading = loadingState.count > 0;
    if (loadingState.loading === true) {
      loadingOverlay.open();
    } else {
      loadingOverlay.close();
    }
    return loadingState;
  };

  const SanitizePost = (payload: any): any => {
    delete payload.createdBy;
    delete payload.createdDate;
    delete payload.updatedBy;
    delete payload.updatedDate;
    return payload;
  };

  const UpdateLoaderFinally = async (loaderState, showLoader: boolean) => {
    if (showLoader === true) {
      loaderState = GetLoaderState(false);
      await setLoading(loaderState);
    }
  };

  const HttpPost = async (
    url: string,
    payload: any,
    showLoader: boolean = true,
    contentType?: string
  ): Promise<ApiResponse> => {
    let loaderState;
    let headers = GetHeaders();
    if (
      contentType !== null ||
      contentType !== undefined ||
      contentType !== ''
    ) {
      headers.headers['Content-Type'] = contentType;
    }
    if (showLoader === true) {
      loaderState = GetLoaderState(true);
      await setLoading(loaderState);
    }
    try {
      payload = SanitizePost(payload);
      const res = await axios.post(url, payload, headers);
      return Response(res);
    } catch (error) {
      return ErrorResponse(error);
    } finally {
      await UpdateLoaderFinally(loaderState, showLoader);
    }
  };

  const HttpGet = async (
    url: string,
    showLoader: boolean = true
  ): Promise<ApiResponse> => {
    let loaderState;
    let headers = GetHeaders();
    if (showLoader === true) {
      loaderState = GetLoaderState(true);
      await setLoading(loaderState);
    }
    try {
      const res = await axios.get(url, headers);
      return Response(res);
    } catch (error) {
      return ErrorResponse(error);
    } finally {
      await UpdateLoaderFinally(loaderState, showLoader);
    }
  };

  const HttpDelete = async (
    url: string,
    showLoader: boolean = true
  ): Promise<ApiResponse> => {
    let loaderState;
    let headers = GetHeaders();
    if (showLoader === true) {
      loaderState = GetLoaderState(true);
      await setLoading(loaderState);
    }
    try {
      const res = await axios.delete(url, headers);
      return Response(res);
    } catch (error) {
      return ErrorResponse(error);
    } finally {
      await UpdateLoaderFinally(loaderState, showLoader);
    }
  };

  const HttpPut = async (
    url: string,
    payload: any,
    showLoader: boolean = true
  ): Promise<ApiResponse> => {
    let loaderState;
    let headers = GetHeaders();
    if (showLoader === true) {
      loaderState = GetLoaderState(true);
      await setLoading(loaderState);
    }
    try {
      payload = SanitizePost(payload);
      const res = await axios.put(url, payload, headers);
      return Response(res);
    } catch (error) {
      return ErrorResponse(error);
    } finally {
      await UpdateLoaderFinally(loaderState, showLoader);
    }
  };

  const HttpPutRaw = async (
    url: string,
    file: File,
    contentType: string,
    showLoader: boolean = true
  ): Promise<ApiResponse> => {
    let loaderState;
    let headers = GetHeaders();
    const azureHeaders = {
      headers: {
        'x-ms-blob-type': 'BlockBlob',
        'Content-Type': contentType,
      },
    };

    if (showLoader === true) {
      loaderState = GetLoaderState(true);
      await setLoading(loaderState);
    }

    try {
      const res = await axios.put(url, file, azureHeaders);

      if (res.status === 201 || res.status === 200) {
        response.data = { hasError: false, hasWarning: false };
        response.message = ApiMessages.Success;
        response.status = res.status;
      }

      return response;
    } catch (error) {
      return ErrorResponse(error);
    } finally {
      await UpdateLoaderFinally(loaderState, showLoader);
    }
  };

  return { HttpPost, HttpGet, HttpDelete, HttpPut, HttpPutRaw };
};

export { HttpService };
